import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { motion } from "framer-motion";
import Skeleton from "react-loading-skeleton";
import {
  ArrowLeft, Clock, CreditCard, FileText, Package,
  ShoppingBag, Tag, Truck, CheckCircle2,
  XCircle, AlertCircle, Loader2, MessageSquare, Send,
} from "lucide-react";
import { cancelOrder, getOrderById, initiateEpsPayment, initiateStripePayment, submitReview, updateReview, getMyOrderReview } from "./services";
import { REVIEW_RATINGS } from "../../services/normalizers";
import useUserRegion from "../../customHooks/useUserRegion";
import "./index.css";

/* ── Helpers ─────────────────────────────────── */
const fmt = (v = 0) => `৳${Number(v || 0).toLocaleString("en-BD")}`;
const fmtD = (v) => {
  if (!v) return "—";
  try {
    return new Date(v).toLocaleString("en-US", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const STATUS_CFG = {
  pending:    { label: "Pending",    color: "#92400e", bg: "#fef3c7", dot: "#f59e0b", icon: <AlertCircle size={14} /> },
  processing: { label: "Processing", color: "#1e40af", bg: "#dbeafe", dot: "#0d6efd", icon: <Loader2    size={14} /> },
  confirmed:  { label: "Confirmed",  color: "#1e40af", bg: "#dbeafe", dot: "#0d6efd", icon: <CheckCircle2 size={14} /> },
  completed:  { label: "Completed",  color: "#166534", bg: "#dcfce7", dot: "#059669", icon: <CheckCircle2 size={14} /> },
  delivered:  { label: "Delivered",  color: "#166534", bg: "#dcfce7", dot: "#059669", icon: <Truck       size={14} /> },
  cancelled:  { label: "Cancelled",  color: "#991b1b", bg: "#fee2e2", dot: "#ef4444", icon: <XCircle     size={14} /> },
  failed:     { label: "Failed",     color: "#991b1b", bg: "#fee2e2", dot: "#ef4444", icon: <XCircle     size={14} /> },
};

const PAYMENT_CFG = {
  paid:        { label: "Paid",    color: "#166534", bg: "#dcfce7" },
  cod_pending: { label: "COD",     color: "#92400e", bg: "#fef3c7" },
  pending:     { label: "Pending", color: "#92400e", bg: "#fef3c7" },
  failed:      { label: "Failed",  color: "#991b1b", bg: "#fee2e2" },
  unpaid:      { label: "Unpaid",  color: "#991b1b", bg: "#fee2e2" },
  refunded:    { label: "Refunded", color: "#1e40af", bg: "#dbeafe" },
};

// `label` carries the API's finer-grained status (Preparing, Ready, Out for
// delivery…) which all share the "processing" colour bucket.
function StatusChip({ status, label, large }) {
  const cfg = STATUS_CFG[status?.toLowerCase()] || STATUS_CFG.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      background: cfg.bg, color: cfg.color, borderRadius: 20,
      padding: large ? "5px 14px" : "3px 10px",
      fontSize: large ? 13 : 11, fontWeight: 700,
      fontFamily: "'Inter', sans-serif",
    }}>
      {cfg.icon} {label || cfg.label}
    </span>
  );
}

function InfoRow({ label, value, bold, accent }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid #f8fafc" }}>
      <span style={{ fontSize: 13, color: "#6b7280", fontFamily: "'Inter', sans-serif" }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: bold ? 800 : 600, color: accent || "#111827", fontFamily: "'Inter', sans-serif" }}>
        {value}
      </span>
    </div>
  );
}

/* ── Skeleton ─────────────────────────────────── */
function PageSkeleton() {
  return (
    <div className="home-content-shell page-navbar-space pb-5">
      <Skeleton height={20} width={60} style={{ marginBottom: 20 }} />
      <Skeleton height={28} width={280} style={{ marginBottom: 6 }} />
      <Skeleton height={16} width={180} style={{ marginBottom: 28 }} />
      <div className="od-layout">
        <div className="od-main">
          <div className="od-card">
            {[1,2,3].map(i => (
              <div key={i} style={{ display: "flex", gap: 12, marginBottom: 12 }}>
                <Skeleton width={56} height={56} borderRadius={8} />
                <div style={{ flex: 1 }}>
                  <Skeleton height={16} width="60%" style={{ marginBottom: 6 }} />
                  <Skeleton height={13} width="35%" />
                </div>
                <Skeleton height={16} width={60} />
              </div>
            ))}
          </div>
          <div className="od-card" style={{ marginTop: 14 }}>
            <Skeleton height={18} width={100} style={{ marginBottom: 14 }} />
            {[1,2].map(i => <Skeleton key={i} height={14} style={{ marginBottom: 8 }} />)}
          </div>
        </div>
        <div className="od-sidebar">
          <div className="od-card">
            <Skeleton height={18} width={120} style={{ marginBottom: 16 }} />
            {[1,2,3,4].map(i => <Skeleton key={i} height={14} style={{ marginBottom: 10 }} />)}
            <Skeleton height={1} style={{ marginBottom: 10 }} />
            <Skeleton height={22} width="60%" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Review Section ───────────────────────────── */
// The API's rating codes (not_tasty | tasty | delicious) with their labels.
const TASTE_OPTIONS = REVIEW_RATINGS;
const MIN_REVIEW_LENGTH = 20;

function TastePicker({ value, onChange, disabled }) {
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {TASTE_OPTIONS.map(({ code, label, color }) => {
        const active = value === code;
        return (
          <button
            key={code}
            type="button"
            disabled={disabled}
            onClick={() => !disabled && onChange(code)}
            style={{
              padding: "8px 18px", borderRadius: 20,
              border: `2px solid ${active ? color : "#e5e7eb"}`,
              background: active ? color : "#fff",
              color: active ? "#fff" : "#374151",
              fontSize: 13, fontWeight: 600,
              cursor: disabled ? "default" : "pointer",
              fontFamily: "'Inter', sans-serif",
              transition: "all 0.15s",
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function ReviewSection({ order }) {
  const [existingReview, setExistingReview] = useState(null);
  const [loadingReview, setLoadingReview]   = useState(true);
  const [rating, setRating]                 = useState("");
  const [content, setContent]               = useState("");
  const [submitting, setSubmitting]         = useState(false);
  const [editing, setEditing]               = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingReview(true);
      try {
        // The API resolves a review straight from the order id.
        const found = await getMyOrderReview(order._id);
        if (found) {
          setExistingReview(found);
          setRating(found.rating);
          setContent(found.content || "");
        }
      } catch {
        /* no review yet */
      }
      setLoadingReview(false);
    })();
  }, [order._id]);

  const handleSubmit = async () => {
    if (!rating) { toast.warning("Please select a rating."); return; }
    if (content.trim().length < MIN_REVIEW_LENGTH) {
      toast.warning(`Please write at least ${MIN_REVIEW_LENGTH} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      if (existingReview) {
        const res = await updateReview(existingReview._id, { rating, content: content.trim() });
        if (res?.status) {
          setExistingReview(res.data || { ...existingReview, rating, content: content.trim() });
          setEditing(false);
          toast.success("Review updated!");
        } else {
          toast.error(res?.message || "Failed to update review.");
        }
      } else {
        const res = await submitReview({ orderId: order._id, rating, content: content.trim() });
        if (res?.status) {
          setExistingReview(res.data);
          setEditing(false);
          toast.success("Review submitted!");
        } else {
          toast.error(res?.message || "Failed to submit review.");
        }
      }
    } catch (e) {
      toast.error(e?.message || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartEdit = () => {
    setRating(existingReview.rating);
    setContent(existingReview.content);
    setEditing(true);
  };

  if (loadingReview) return null;

  const showForm = !existingReview || editing;

  return (
    <motion.div
      className="od-card"
      style={{ marginTop: 14 }}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
    >
      <h6 style={{
        fontWeight: 800, fontSize: 15, margin: "0 0 16px",
        fontFamily: "'Inter', sans-serif", color: "#1a1a2e",
        display: "flex", alignItems: "center", gap: 6,
      }}>
        <MessageSquare size={16} color="#0d6efd" />
        {existingReview && !editing ? "Your Review" : "Write a Review"}
      </h6>

      {/* Submitted review (read mode) */}
      {existingReview && !editing && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            {(() => {
              const opt = TASTE_OPTIONS.find((o) => o.code === existingReview.rating);
              return (
                <span style={{
                  padding: "5px 14px", borderRadius: 20,
                  background: opt?.color || "#e5e7eb",
                  color: "#fff", fontSize: 13, fontWeight: 700,
                  fontFamily: "'Inter', sans-serif",
                }}>
                  {opt?.label || existingReview.rating}
                </span>
              );
            })()}
          </div>
          <p style={{
            fontSize: 13, color: "#374151", lineHeight: 1.6, margin: "0 0 14px",
            fontFamily: "'Inter', sans-serif", background: "#f9fafb",
            borderRadius: 9, padding: "10px 13px",
          }}>
            {existingReview.content}
          </p>
          <button
            onClick={handleStartEdit}
            style={{
              padding: "8px 16px", borderRadius: 9, border: "1.5px solid #e5e7eb",
              background: "#fff", fontSize: 12, fontWeight: 600, color: "#374151",
              cursor: "pointer", fontFamily: "'Inter', sans-serif",
            }}
          >
            Edit Review
          </button>
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div>
          <div style={{ marginBottom: 14 }}>
            <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: "#6b7280", fontFamily: "'Inter', sans-serif", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Rating
            </p>
            <TastePicker value={rating} onChange={setRating} disabled={submitting} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: "#6b7280", fontFamily: "'Inter', sans-serif", textTransform: "uppercase", letterSpacing: 0.5 }}>
              Your Review
            </p>
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={submitting}
              placeholder={`Share your experience with this order… (at least ${MIN_REVIEW_LENGTH} characters)`}
              rows={3}
              style={{
                width: "100%", padding: "10px 13px", borderRadius: 9,
                border: "1.5px solid #e5e7eb", fontSize: 13, fontFamily: "'Inter', sans-serif",
                color: "#111827", resize: "vertical", outline: "none",
                boxSizing: "border-box", lineHeight: 1.5,
                background: submitting ? "#f9fafb" : "#fff",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            {editing && (
              <button
                onClick={() => setEditing(false)}
                disabled={submitting}
                style={{
                  padding: "9px 16px", borderRadius: 9, border: "1.5px solid #e5e7eb",
                  background: "#fff", fontSize: 13, fontWeight: 600, color: "#6b7280",
                  cursor: "pointer", fontFamily: "'Inter', sans-serif",
                }}
              >
                Cancel
              </button>
            )}
            <button
              onClick={handleSubmit}
              disabled={submitting || !rating || content.trim().length < MIN_REVIEW_LENGTH}
              style={{
                padding: "9px 20px", borderRadius: 9, border: "none",
                background: submitting || !rating || content.trim().length < MIN_REVIEW_LENGTH ? "#d1d5db" : "#0d6efd",
                color: "#fff", fontSize: 13, fontWeight: 700,
                cursor: submitting || !rating || content.trim().length < MIN_REVIEW_LENGTH ? "not-allowed" : "pointer",
                fontFamily: "'Inter', sans-serif",
                display: "flex", alignItems: "center", gap: 6,
              }}
            >
              <Send size={13} />
              {submitting ? "Submitting…" : existingReview ? "Update Review" : "Submit Review"}
            </button>
          </div>
        </div>
      )}
    </motion.div>
  );
}

/* ── Main ─────────────────────────────────────── */
export default function OrderDetails() {
  const { id } = useParams();
  const [order, setOrder]                   = useState(null);
  const [isCancelling, setIsCancelling]     = useState(false);
  const [cancelReason, setCancelReason]     = useState("");
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);
  const [isRetrying, setIsRetrying]         = useState(false);
  const { isBangladesh, loading: regionLoading } = useUserRegion();

  useEffect(() => {
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await getOrderById(id);
        setOrder(res?.data);
      } catch { setError("Failed to fetch order"); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <PageSkeleton />;
  if (error)   return <div className="home-content-shell page-navbar-space pb-5" style={{ color: "#ef4444", fontFamily: "'Inter', sans-serif" }}>{error}</div>;
  if (!order)  return <div className="home-content-shell page-navbar-space pb-5" style={{ fontFamily: "'Inter', sans-serif" }}>Order not found.</div>;

  /* payment logic — the API records intent ("cod" vs "online"); which gateway
     handles an online order is decided when the payment is initiated. */
  const orderItems   = order?.items || [];
  const payStatus    = String(order?.paymentStatus || "").toLowerCase();
  const isCod        = order?.paymentMethod === "cod";
  const effectiveMethod = isCod ? "cod" : isBangladesh ? "eps" : "stripe";
  const canRetry = ["pending", "failed"].includes(payStatus) && !isCod;
  // The API lets a customer cancel their own order only before the kitchen
  // starts cooking it; everything later is the kitchen's or support's call.
  const canCancel = ["pending", "confirmed"].includes(order?.rawStatus);

  const handleCancel = async () => {
    if (!window.confirm("Cancel this order? This can't be undone.")) return;
    try {
      setIsCancelling(true);
      const res = await cancelOrder(order._id, cancelReason.trim() || undefined);
      if (res?.status) {
        setOrder(res.data);
        setCancelReason("");
        toast.success(res.message || "Order cancelled.");
      } else {
        toast.error(res?.message || "Could not cancel this order.");
      }
    } catch (e) {
      toast.error(e?.message || "Could not cancel this order.");
    } finally {
      setIsCancelling(false);
    }
  };

  const handlePayNow = async () => {
    if (effectiveMethod === "cod") { toast.warning("Cash on Delivery orders do not require online payment."); return; }
    if (regionLoading) { toast.warning("Detecting your region..."); return; }
    try {
      setIsRetrying(true);
      const res = effectiveMethod === "eps"
        ? await initiateEpsPayment(order._id)
        : await initiateStripePayment(order._id);
      if (res?.status && res?.data?.redirectUrl) { window.location.href = res.data.redirectUrl; return; }
      toast.error(res?.message || "Failed to initiate payment.");
    } catch (e) { toast.error(e?.message || "Failed to initiate payment."); }
    finally { setIsRetrying(false); }
  };

  const statusCfg = STATUS_CFG[order?.status?.toLowerCase()] || STATUS_CFG.pending;
  const paymentLabelCfg = PAYMENT_CFG[payStatus] || PAYMENT_CFG.pending;

  return (
    <div className="home-content-shell page-navbar-space pb-5">

      {/* Back */}
      <Link to="/my-orders" style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        fontSize: 13, fontWeight: 600, color: "#6b7280",
        textDecoration: "none", fontFamily: "'Inter', sans-serif", marginBottom: 16,
      }}>
        <ArrowLeft size={15} /> My Orders
      </Link>

      {/* Page header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
        <div>
          <h5 style={{ fontWeight: 800, fontSize: 22, margin: "0 0 4px", fontFamily: "'Inter', sans-serif", color: "#0f172a" }}>
            Order #{order?.orderNumber || order?._id?.slice(-8)?.toUpperCase()}
          </h5>
          <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
            Placed {fmtD(order?.createdAt)}
          </span>
        </div>
        <StatusChip status={order?.status} label={order?.statusLabel} large />
      </div>

      {/* Two-column layout */}
      <div className="od-layout">

        {/* ── LEFT: items + payment ── */}
        <div className="od-main">

          {/* Order items */}
          <div className="od-card">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <h6 style={{ fontWeight: 800, fontSize: 15, margin: 0, fontFamily: "'Inter', sans-serif", color: "#1a1a2e", display: "flex", alignItems: "center", gap: 6 }}>
                <ShoppingBag size={16} color="#0d6efd" /> Order Items
              </h6>
              <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", background: "#f1f5f9", borderRadius: 20, padding: "2px 9px", fontFamily: "'Inter', sans-serif" }}>
                {orderItems.length} item{orderItems.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {orderItems.map((item, i) => (
                <motion.div
                  key={item?._id || i}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "10px 0",
                    borderBottom: i < orderItems.length - 1 ? "1px solid #f8fafc" : "none",
                  }}
                >
                  {/* Food image */}
                  {item?.image ? (
                    <img src={item.image} alt={item.name}
                      style={{ width: 52, height: 52, borderRadius: 8, objectFit: "cover", border: "1px solid #f1f5f9", flexShrink: 0 }}
                    />
                  ) : (
                    <div style={{ width: 52, height: 52, borderRadius: 8, background: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <Package size={22} color="#d1d5db" />
                    </div>
                  )}

                  {/* Name + qty */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: "0 0 3px", fontSize: 13, fontWeight: 700, color: "#111827", fontFamily: "'Inter', sans-serif", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item?.name || "Food item"}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", fontFamily: "'Inter', sans-serif" }}>
                      {fmt(item?.price)} × {item?.quantity}
                    </p>
                  </div>

                  {/* Line total */}
                  <span style={{ fontSize: 14, fontWeight: 800, color: "#111827", fontFamily: "'Inter', sans-serif", flexShrink: 0 }}>
                    {fmt((item?.price || 0) * (item?.quantity || 1))}
                  </span>
                </motion.div>
              ))}

              {!orderItems.length && (
                <p style={{ fontSize: 13, color: "#9ca3af", margin: 0, fontFamily: "'Inter', sans-serif" }}>No items found.</p>
              )}
            </div>
          </div>

          {/* Review */}
          {order?.status?.toLowerCase() === "delivered" && (
            <ReviewSection order={order} />
          )}

          {/* Payment */}
          <div className="od-card" style={{ marginTop: 14 }}>
            <h6 style={{ fontWeight: 800, fontSize: 15, margin: "0 0 14px", fontFamily: "'Inter', sans-serif", color: "#1a1a2e", display: "flex", alignItems: "center", gap: 6 }}>
              <CreditCard size={16} color="#0d6efd" /> Payment
            </h6>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <InfoRow label="Status" value={
                <span style={{ background: paymentLabelCfg.bg, color: paymentLabelCfg.color, borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700 }}>
                  {paymentLabelCfg.label}
                </span>
              } />
              <InfoRow label="Method" value={isCod ? "Cash on Delivery" : "Online payment"} />
              {order?.couponCode && <InfoRow label="Coupon" value={order.couponCode} />}
            </div>

            {canRetry && (
              <button
                onClick={handlePayNow}
                disabled={isRetrying || regionLoading}
                style={{
                  width: "100%", marginTop: 14, padding: "11px",
                  borderRadius: 10, border: "none",
                  background: isRetrying ? "#9ca3af" : "#f59e0b",
                  color: "#fff", fontSize: 14, fontWeight: 700,
                  fontFamily: "'Inter', sans-serif",
                  cursor: isRetrying ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                }}
              >
                <CreditCard size={15} />
                {isRetrying ? "Processing…" : "Pay Now"}
              </button>
            )}
          </div>
        </div>

        {/* ── RIGHT: summary ── */}
        <div className="od-sidebar">
          <div className="od-card">
            <h6 style={{ fontWeight: 800, fontSize: 15, margin: "0 0 14px", fontFamily: "'Inter', sans-serif", color: "#1a1a2e", display: "flex", alignItems: "center", gap: 6 }}>
              <Tag size={15} color="#0d6efd" /> Order Summary
            </h6>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <InfoRow label="Subtotal"     value={fmt(order?.subTotal)} />
              {order?.discount > 0 && (
                <InfoRow label="Discount" value={`— ${fmt(order.discount)}`} accent="#059669" />
              )}
              <InfoRow label="Delivery Fee" value={fmt(order?.deliveryFee)} />
            </div>

            {/* Total */}
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              marginTop: 10, paddingTop: 12, borderTop: "2px solid #f1f5f9",
            }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: "#374151", fontFamily: "'Inter', sans-serif" }}>Total</span>
              <span style={{ fontSize: 20, fontWeight: 900, color: "#111827", fontFamily: "'Inter', sans-serif" }}>{fmt(order?.total)}</span>
            </div>

            {/* Order meta */}
            <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 8 }}>
              {order?.deliveryAddress && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#f9fafb", borderRadius: 8 }}>
                  <Package size={14} color="#9ca3af" />
                  <div>
                    <p style={{ margin: 0, fontSize: 10, color: "#9ca3af", fontFamily: "'Inter', sans-serif", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Deliver To</p>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827", fontFamily: "'Inter', sans-serif" }}>
                      {order.deliveryAddress.label} — {order.deliveryAddress.address}
                    </p>
                  </div>
                </div>
              )}
              {/* Present only while the order is in flight, so the customer can
                  call the kitchen about a delivery that is on its way. */}
              {order?.chefPhone && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: "#f9fafb", borderRadius: 8 }}>
                  <Clock size={14} color="#9ca3af" />
                  <div>
                    <p style={{ margin: 0, fontSize: 10, color: "#9ca3af", fontFamily: "'Inter', sans-serif", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Kitchen Contact</p>
                    <a href={`tel:${order.chefPhone}`} style={{ fontSize: 13, fontWeight: 600, color: "#0d6efd", fontFamily: "'Inter', sans-serif", textDecoration: "none" }}>
                      {order.chefPhone}
                    </a>
                  </div>
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", background: `${statusCfg.bg}88`, borderRadius: 8 }}>
                <span style={{ color: statusCfg.dot }}>{statusCfg.icon}</span>
                <div>
                  <p style={{ margin: 0, fontSize: 10, color: "#9ca3af", fontFamily: "'Inter', sans-serif", textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Order Status</p>
                  <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: statusCfg.color, fontFamily: "'Inter', sans-serif" }}>{order?.statusLabel || statusCfg.label}</p>
                </div>
              </div>
            </div>

            {/* Track order */}
            <Link
              to={`/order-tracking/${order?._id}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                marginTop: 14, padding: "10px", borderRadius: 10,
                border: "1.5px solid #e5e7eb", background: "#fff",
                fontSize: 13, fontWeight: 600, color: "#374151",
                textDecoration: "none", fontFamily: "'Inter', sans-serif",
              }}
            >
              <Truck size={14} /> Track Order
            </Link>

            <Link
              to={`/invoice/${order?._id}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                marginTop: 8, padding: "10px", borderRadius: 10,
                border: "1.5px solid #e5e7eb", background: "#fff",
                fontSize: 13, fontWeight: 600, color: "#374151",
                textDecoration: "none", fontFamily: "'Inter', sans-serif",
              }}
            >
              <FileText size={14} /> View Invoice
            </Link>

            {canCancel && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid #f1f5f9" }}>
                <input
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  disabled={isCancelling}
                  maxLength={500}
                  placeholder="Reason (optional)"
                  style={{
                    width: "100%", padding: "9px 12px", borderRadius: 9,
                    border: "1.5px solid #e5e7eb", fontSize: 13,
                    fontFamily: "'Inter', sans-serif", background: "#f9fafb",
                    outline: "none", boxSizing: "border-box", marginBottom: 8,
                  }}
                />
                <button
                  onClick={handleCancel}
                  disabled={isCancelling}
                  style={{
                    width: "100%", padding: "10px", borderRadius: 10,
                    border: "1.5px solid #fecaca",
                    background: isCancelling ? "#f9fafb" : "#fff",
                    color: "#b91c1c", fontSize: 13, fontWeight: 700,
                    fontFamily: "'Inter', sans-serif",
                    cursor: isCancelling ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}
                >
                  <XCircle size={14} />
                  {isCancelling ? "Cancelling…" : "Cancel Order"}
                </button>
                <p style={{
                  margin: "6px 0 0", fontSize: 11, color: "#9ca3af",
                  textAlign: "center", fontFamily: "'Inter', sans-serif",
                }}>
                  You can cancel until the kitchen starts preparing your food.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
